import dateFormat from 'dateformat'
import { path } from 'ramda'
import {
  Balcony,
  RentPeriod,
  Condition,
  Storage,
  BuildingType,
  SecurityDeposit,
  WaterFeeBasis
} from '../../../../types/propValues/apartments'
import { Showing, ShowingType } from '../../../../types/showings'

export interface BuildingInfoDisplay {
  price: string
  rooms: string
  squareMeters: string
  floor?: string
  buildYear: number
  condition: Condition
  storage: Storage
  buildingType: BuildingType
  balcony: Balcony
  energyCertification: string
}

export const buildingInfoOrder = [
  'price',
  'rooms',
  'squareMeters',
  'condition',
  'balcony',
  'buildingType',
  'energyCertification',
  'buildYear',
  'floor',
  'laundryMachine',
  'dishwasher'
]

export const additionalPropsOrder = [
  'laundryMachine',
  'dishwasher',
  'sauna',
  'petsAllowed'
]
/** Used to allow for different types for display props
 *
 * example number price 15 --> string price '15€'
 */
export interface ContractDisplayProps {
  /** ISO date string */
  availableFromDate?: string // if not defined, per contract
  /** ISO date string example 2020/12/24 */
  availableUntilDate?: string
  rentPeriod: RentPeriod
  securityDeposit: SecurityDeposit
  securityDepositAmount?: string // not required, if securityDeposit === 'other'
  waterFeeBasis: WaterFeeBasis
  /** shown if waterFeeBasis !== 'fixed',
   * Required if waterFeeBasis === 'perTenant' or 'perApartment',
   *
   * Shown but not required if waterFeeBasis === 'metered' */
  waterFee?: string
  parkingFee?: string // not required, only shown if parking !== 'no' or 'not sure'
  requirePriceEstimate: string
  showingType: ShowingType
  propertyManager: string | undefined
  renovations?: string
  notes?: string
  noticeTime?: string
}

/** returns list to map through, use undefined to parse not defined values */
export const getContractDetailsOrder = ({
  availableFromDate,
  availableUntilDate,
  waterFee,
  waterFeeBasis,
  parkingFee,
  securityDepositAmount,
  propertyManager,
  renovations,
  notes,
  noticeTime
}: ContractDisplayProps) => [
  availableFromDate ? 'availableFromDate' : undefined,
  availableUntilDate ? 'availableUntilDate' : undefined,
  'waterFeeBasis',
  waterFee !== undefined // show if waterFee is zero, if metered and no advance payment
    ? waterFeeBasis === 'metered'
      ? 'advanceWaterFee' // if metered, tenants pay prepayment which is adjusted by meter
      : 'waterFee'
    : undefined,
  'rentPeriod',
  noticeTime ? 'noticeTime' : undefined,
  'securityDeposit',
  securityDepositAmount ? 'securityDepositAmount' : undefined,
  parkingFee ? 'parkingFee' : undefined,
  // 'showingType',
  propertyManager ? 'propertyManager' : undefined,
  notes ? 'notes' : undefined
]

export const privateClientInfoOrder = [
  'name',
  'phoneNumber',
  'email',
  'address',
  'postalArea',
  'language',
  'iban'
]
export const realtorClientInfoOrder = [
  'clientName',
  'clientPhone',
  'clientEmail',
  'address',
  'postalArea',
  'language',
  'iban'
]

export const businessClientInfoOrder = [
  'name',
  'companyName',
  'businessId',
  'address',
  'postalArea',
  'phoneNumber',
  'email',
  'language',
  'iban'
]
export const businessRealtorClientInfoOrder = [
  'clientName',
  'clientPhone',
  'clientEmail',
  'companyName',
  'businessId',
  'address',
  'postalArea',
  'language',
  'iban'
]

export const noMainOwnerClientInfoOrder = [
  'name',
  'phoneNumber',
  'email',
  'language'
]

export const getDateTitle = (showing: Showing) => {
  const startDate = dateFormat(path(['startDate'], showing), 'dd.mm.yyyy HH:MM')
  const endDate = dateFormat(path(['endDate'], showing), 'HH:MM')
  return `${startDate} - ${endDate}`
}
